/**
 * comum.js — regras de apresentação compartilhadas pelo Squad Designer, pelo Gate de
 * Squads e pelo Catálogo (SPEC A2). Funções puras, sem I/O.
 */

/** Custo estimado por membro (US$), por modelo — mesma tabela do Designer e da API. */
export const CUSTO_POR_MODELO = { opus: 3.0, sonnet: 1.8, haiku: 0.6 };

export const MODELOS = ['opus', 'sonnet', 'haiku'];

/** US$ com `toLocaleString('pt-BR')` (NFR-5). */
export function usd(n) {
  const v = Number.isFinite(Number(n)) ? Number(n) : 0;
  return `US$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function custoDoModelo(model) {
  return CUSTO_POR_MODELO[model] ?? CUSTO_POR_MODELO.sonnet;
}

/** FR-16: o que cada perfil de ferramentas deixa o membro fazer. */
export const TEXTO_DO_PERFIL = {
  leitura: 'lê o código, não edita',
  edicao: 'edita arquivos',
  'edicao+shell': 'edita e roda comandos',
  revisao: 'lê e roda análise, não edita',
};

export const PERFIS = ['leitura', 'edicao', 'edicao+shell', 'revisao'];

/**
 * Origem legível do prompt efetivo (FR-13): "do projeto v3", "do tenant v1",
 * "do catálogo", "sem prompt".
 */
export function origemLegivel(origem, version) {
  const v = version != null ? ` v${version}` : '';
  switch (origem) {
    case 'projeto':
    case 'project':
      return `do projeto${v}`;
    case 'tenant':
      return `do tenant${v}`;
    case 'catalogo_tenant':
    case 'catalogo_global':
    case 'catalogo':
      return 'do catálogo';
    default:
      return 'sem prompt';
  }
}

/**
 * Selo do membro (FR-7), derivado SÓ da conferência (FR-6). Se a API já mandou `selo`,
 * ele vale; senão aplica a mesma regra: rascunho/sem_prompt → sem_prompt; incompleto →
 * incompleto; conflito com regras → conflito; senão especialista.
 */
export function seloDoMembro(membro) {
  if (!membro) return null;
  if (membro.selo) return membro.selo;
  const status = membro.prompt?.status;
  if (membro.status_catalogo === 'rascunho' || status === 'sem_prompt' || !status) return 'sem_prompt';
  if (status !== 'especialista') return 'incompleto';
  if ((membro.prompt?.conflitos || []).length) return 'conflito';
  return 'especialista';
}

export const TEXTO_DO_SELO = {
  especialista: '✅ especialista',
  sem_prompt: '⚠️ sem prompt de especialista',
  incompleto: '⚠️ incompleto',
  conflito: '⚠️ conflito com regras',
};

/** Rótulo pt-BR da origem do responsável pela etapa (FR-11). */
export const TEXTO_DA_ORIGEM_RESP = {
  aprovado: 'aprovado',
  proposto: 'proposto',
  especialista_catalogo: 'especialista do catálogo',
  papel_padrao: 'papel genérico',
};

// ─── Especialista × genérico ────────────────────────────────────────────────

/**
 * Os 7 papéis de camada SEM tecnologia — espelha a regra da API (`tipo: "generico"`).
 * Só vale como fallback quando o item não traz `tipo`.
 */
export const PAPEIS_GENERICOS = new Set([
  'backend-developer', 'frontend-developer', 'data-engineer', 'devops-engineer',
  'test-automator', 'mobile-developer', 'documentation-engineer',
]);

export const TEXTO_DO_TIPO = { especialista: 'Especialista', generico: 'Genérico' };

export const TITULO_DO_TIPO = {
  especialista: 'Especialista: domina a tecnologia/ofício; o blueprint prevalece.',
  generico: 'Genérico: papel de camada sem tecnologia — usado só quando não há especialista.',
};

/**
 * Tipo do agente: o `tipo` que a API mandou (especialista|generico) vale; senão a regra
 * local pelo slug (`name`/`agent`/`role`/`slug`, ou a própria string).
 */
export function tipoDoAgente(agente) {
  if (agente && typeof agente === 'object' && (agente.tipo === 'especialista' || agente.tipo === 'generico')) {
    return agente.tipo;
  }
  const slug = typeof agente === 'string'
    ? agente
    : (agente?.name ?? agente?.agent ?? agente?.role ?? agente?.slug ?? '');
  return PAPEIS_GENERICOS.has(String(slug).trim().toLowerCase()) ? 'generico' : 'especialista';
}

const PESO_DO_PROMPT = { especialista: 0, incompleto: 1, sem_prompt: 2 };

/** `prompt_status` do item: o do catálogo (`prompt_status`) ou o da conferência (`prompt.status`). */
function statusDoPrompt(agente) {
  return agente?.prompt_status ?? agente?.prompt?.status ?? null;
}

/**
 * Ordena para ESCOLHA: especialistas primeiro (prompt especialista → incompleto → sem
 * prompt → desconhecido) e genéricos por último. Estável: empates mantêm a ordem de entrada.
 */
export function ordenarPorEspecialidade(lista) {
  const peso = (a) => {
    const base = tipoDoAgente(a) === 'generico' ? 10 : 0;
    const st = statusDoPrompt(a);
    return base + (st in PESO_DO_PROMPT ? PESO_DO_PROMPT[st] : 3);
  };
  return (lista || [])
    .map((a, i) => ({ a, i, p: peso(a) }))
    .sort((x, y) => x.p - y.p || x.i - y.i)
    .map((x) => x.a);
}

/**
 * Catálogo agrupado por CATEGORIA para o seletor (pedido do dono, 2026-10-03: com 397 nomes
 * soltos a pessoa se perde e escolhe errado). Grupos em ordem alfabética, "Sem categoria" por
 * último; dentro de cada grupo mantém a ordem recebida (especialistas primeiro).
 */
export function agruparPorCategoria(lista) {
  const grupos = new Map();
  for (const a of lista || []) {
    const cat = String(a?.category || '').trim() || 'Sem categoria';
    if (!grupos.has(cat)) grupos.set(cat, []);
    grupos.get(cat).push(a);
  }
  return [...grupos.entries()]
    .sort(([x], [y]) => (x === 'Sem categoria') - (y === 'Sem categoria') || x.localeCompare(y, 'pt-BR'))
    .map(([categoria, agentes]) => ({ categoria, agentes }));
}

/** Descrição curta (sem markdown) para caber na opção do seletor; o corte é marcado com "…". */
export function descricaoCurta(texto, limite = 70) {
  const t = String(texto || '').replace(/[*_`#]/g, '').replace(/\s+/g, ' ').trim();
  return t.length <= limite ? t : `${t.slice(0, limite - 1).trimEnd()}…`;
}

/** Texto do tipo para dentro de `<option>` (não aceita componentes): "[Especialista] ". */
export function prefixoDoTipo(agente) {
  return `[${TEXTO_DO_TIPO[tipoDoAgente(agente)]}] `;
}

/**
 * Skills para escolha: com conteúdo primeiro, desconhecidas no meio, sem conteúdo por
 * último. `temConteudo(nome)` → true | false | undefined. Estável.
 */
export function ordenarSkillsPorConteudo(nomes, temConteudo) {
  const peso = (n) => {
    const v = temConteudo(n);
    if (v === true) return 0;
    if (v === false) return 2;
    return 1;
  };
  return (nomes || [])
    .map((n, i) => ({ n, i, p: peso(n) }))
    .sort((x, y) => x.p - y.p || x.i - y.i)
    .map((x) => x.n);
}

/** Itens de lista de uma resposta `{items}` ou array; ignora o que não for objeto. */
export function itensDe(data) {
  const arr = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
  return arr.filter((x) => x && typeof x === 'object');
}

/** Membros do design (orquestração + squads), sem repetição — espelha a API. */
export function membrosDoDesign(squads, nodes) {
  const vistos = new Map();
  for (const n of nodes || []) {
    if (n?.type === 'agent' && !n.squad && n.agent) {
      const slug = String(n.agent).trim();
      if (!vistos.has(slug)) vistos.set(slug, { agent: slug, label: n.label || slug, nodeId: n.id, squadId: null });
    }
  }
  for (const sq of squads || []) {
    for (const ag of sq?.agents || []) {
      const slug = String(ag?.role || '').trim();
      if (slug && !vistos.has(slug)) {
        vistos.set(slug, { agent: slug, label: ag.label || slug, squadId: sq.id, agentId: ag.id });
      }
    }
  }
  return [...vistos.values()];
}

/**
 * Membros do design agrupados como aparecem no canvas: orquestração e cada squad com
 * TODOS os seus membros — o mesmo agente em dois squads aparece nos dois grupos.
 * Grupo sem membro fica de fora.
 */
export function gruposDeMembros(squads, nodes) {
  const grupos = [];
  const orquestracao = (nodes || [])
    .filter((n) => n?.type === 'agent' && !n.squad && n.agent)
    .map((n) => ({ agent: String(n.agent).trim(), label: n.label || String(n.agent).trim() }));
  if (orquestracao.length) grupos.push({ id: 'orquestracao', nome: 'Orquestração', membros: orquestracao });
  for (const sq of squads || []) {
    const membros = (sq?.agents || [])
      .filter((ag) => String(ag?.role || '').trim())
      .map((ag) => ({ agent: String(ag.role).trim(), label: ag.label || String(ag.role).trim() }));
    if (membros.length) grupos.push({ id: sq.id, nome: sq.name || sq.id, membros });
  }
  return grupos;
}

/** Mensagem pt-BR de um ApiError (problem+json no topo do corpo). */
export function mensagemDoErro(error, fallback = 'Não foi possível concluir a ação. Tente novamente.') {
  if (!error) return null;
  if (error.status === 403) return 'Sem permissão para esta ação.';
  const detail = error.problemBody?.detail ?? error.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
}
